import { useState } from "react";
import Iconify from "~src/components/Iconify";
//@mui
import {
  Box,
  IconButton,
  DialogActions,
  DialogTitle,
  Button,
  useMediaQuery,
  useTheme,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
} from "@mui/material";
import { Accordion } from "@mui/material";
import DateRangeSelector from "./DateRangeSelector";
import { PriceRangeSelector } from "./PriceRangeSelector";
import { BestSellingProductsFilters } from "~src/pages/winningProducts/Products";
import { BrandResult, SearchBar } from "./SearchBar";
import CategorySelect from "./CategorySelector";
import { ProductRankingSelector } from "./ProductRankingSelector";
import RegionSelect from "~src/components/RegionSelect";
import { CountryRegion, COUNTRIES, REGIONS } from "~src/data/countries";

type PropsType = {
  filters: BestSellingProductsFilters | undefined;
  closeDrawer: () => void;
  onCancel?: VoidFunction;
  onComplete: (filters: BestSellingProductsFilters) => void;
  selected: BrandResult[];
  setSelected: React.Dispatch<React.SetStateAction<BrandResult[]>>;
};

interface TransformedCategory {
  category_id: number;
  category_title: string;
  sub_category_title: string;
  sub_category_id: number;
}

// Helper function to convert country codes to CountryRegion objects
const convertCountryCodesToRegions = (countryCodes: string[]): CountryRegion[] => {
  return countryCodes.map((code: string) => {
    // Check if it's a region
    const regionMatch = REGIONS.find(region => region === code);
    if (regionMatch) {
      return {
        code,
        label: regionMatch,
        groupBy: 'REGION'
      };
    }

    // Check if it's a country
    const countryMatch = COUNTRIES.find(country => country.code === code);
    if (countryMatch) {
      return {
        code,
        label: countryMatch.label,
        groupBy: 'COUNTRY'
      };
    }

    // Fallback for unknown codes
    return {
      code,
      label: code,
      groupBy: 'COUNTRY'
    };
  });
};

// Helper function to convert category string to TransformedCategory array
const convertCategoryStringToCategories = (categoryString: string): TransformedCategory[] => {
  const categories = categoryString.split(',');
  return categories.map(cat => ({
    category_id: parseInt(cat),
    category_title: cat,
    sub_category_title: cat,
    sub_category_id: parseInt(cat)
  }));
};

// Helper function to initialize country values from filters
const initCountryValues = (filters?: BestSellingProductsFilters): CountryRegion[] => {
  if (!filters?.country_code) return [];
  const countryCodes = Array.isArray(filters.country_code)
    ? filters.country_code
    : (filters.country_code as string).split(',');
  return convertCountryCodesToRegions(countryCodes);
};

// Helper function to initialize category values from filters
const initCategoryValues = (filters?: BestSellingProductsFilters): TransformedCategory[] => {
  if (!filters?.product_category) return [];
  return convertCategoryStringToCategories(filters.product_category);
};

export default function FilterDrawer({
  filters,
  closeDrawer,
  onCancel,
  onComplete,
  selected,
  setSelected,
}: PropsType) {
  const [localFilters, setLocalFilters] = useState(filters);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [localCategoryValues, setLocalCategoryValues] = useState<
    TransformedCategory[]
  >(() => initCategoryValues(filters));

  const [localCountryValues, setLocalCountryValues] = useState<CountryRegion[]>(
    () => initCountryValues(filters)
  );

  const [prevFilters, setPrevFilters] = useState(filters);

  // Sync state from prop during render (not in useEffect) to avoid extra render cycle
  if (filters !== prevFilters) {
    setPrevFilters(filters);
    if (filters) {
      setLocalFilters(filters);
      setLocalCountryValues(initCountryValues(filters));
      setLocalCategoryValues(initCategoryValues(filters));
    }
  }

  const handleSaveClick = (
    selectedLocalFilters: BestSellingProductsFilters | undefined,
  ) => {
    if (selectedLocalFilters) {
      onComplete(selectedLocalFilters);
    }
  };

  const removeBrandId = (brandIdToRemove: string) => {
    setLocalFilters((prevFilters) => {
      const updatedBrandIds = prevFilters?.brand_ids
        ?.split(",")
        .filter((id) => id !== brandIdToRemove);

      return {
        ...prevFilters,
        brand_ids:
          updatedBrandIds && updatedBrandIds.length > 0
            ? updatedBrandIds.join(",")
            : "",
      };
    });
  };

  return (
    <Box sx={{
      width: isMobile ? "300px" : "500px"
    }}>
      <DialogTitle sx={{ display: "flex" }}>
        <Box>Filter Best Selling Products</Box>
        <Box
          sx={{
            ml: "auto",
          }}
        >
          <IconButton onClick={closeDrawer}>
            <Iconify icon={"material-symbols:close"} width={28} height={28} />
          </IconButton>
        </Box>
      </DialogTitle>
      <Box sx={{ px: 2, my: 1 }}>
        <RegionSelect
          localCountryValues={localCountryValues}
          setLocalCountryValues={setLocalCountryValues}
          onSelect={(values) => {
            setLocalFilters((prevFilters: any) => ({
              ...prevFilters,
              country_code: values.map(v => v.code).join(','),
            }));
          }}
        />
      </Box>
      <Box sx={{ px: 2, my: 1 }}>
        <Accordion sx={{ border: `1px solid ${theme.palette.grey[300]}` }}>
          <AccordionSummary
            expandIcon={
              <Iconify icon={"mdi:arrow-down-drop"} width={25} height={25} />
            }
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Typography sx={{ fontFamily: theme.typography.fontFamily }}>Brands</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <SearchBar
              selected={selected}
              setSelected={setSelected}
              localFilters={localFilters}
              onSelect={(brand_ids: string) => {
                setLocalFilters((prevFilters) => {
                  const existingBrandIds = prevFilters?.brand_ids
                    ? prevFilters.brand_ids.split(",")
                    : [];

                  const updatedBrandIds = [
                    ...new Set([...existingBrandIds, brand_ids]),
                  ];

                  return {
                    ...prevFilters,
                    brand_ids: updatedBrandIds.join(","),
                  };
                });
              }}
              removeBrandId={removeBrandId}
            />
          </AccordionDetails>
        </Accordion>
      </Box>
      <Box sx={{ px: 2, my: 1 }}>
        <CategorySelect
          localValues={localCategoryValues}
          setLocalValues={setLocalCategoryValues}
          onSelect={(value) => {
            if (!value) {
              setLocalFilters((prevFilters: any) => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { product_category, ...rest } = prevFilters || {};
                return rest;
              });
              return;
            }

            setLocalFilters((prevFilters: any) => ({
              ...prevFilters,
              product_category: value.join(","),
            }));
          }}
        />
      </Box>
      <Box sx={{ px: 4 }}>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  !localFilters || !localFilters.new_arrivals
                    ? false
                    : localFilters.new_arrivals
                }
                onChange={(e) => {
                  setLocalFilters({
                    ...localFilters,
                    new_arrivals: e.target.checked ? true : undefined,
                  });
                }}
              />
            }
            label={<Typography sx={{ fontFamily: theme.typography.fontFamily }}>New Arrivals</Typography>}
          />
        </FormGroup>
      </Box>
      <Box sx={{ px: 2, my: 1 }}>
        <Accordion sx={{ border: `1px solid ${theme.palette.grey[300]}` }}>
          <AccordionSummary
            expandIcon={
              <Iconify icon={"mdi:arrow-down-drop"} width={25} height={25} />
            }
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Typography sx={{ fontFamily: theme.typography.fontFamily }}>Product Added to Store</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <DateRangeSelector
              value={localFilters?.product_added}
              onSelect={(value) => {
                setLocalFilters((prevFilters: any) => ({
                  ...prevFilters,
                  product_added: value,
                }));
              }}
            />
          </AccordionDetails>
        </Accordion>
      </Box>
      <Box sx={{ px: 2, my: 1 }}>
        <Accordion sx={{ border: `1px solid ${theme.palette.grey[300]}` }}>
          <AccordionSummary
            expandIcon={
              <Iconify icon={"mdi:arrow-down-drop"} width={25} height={25} />
            }
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Typography sx={{ fontFamily: theme.typography.fontFamily }}>Product Price Filters</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <PriceRangeSelector
              value={localFilters?.product_price}
              onSelect={(value) => {
                setLocalFilters((prevFilters: any) => {
                  if (!value) {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const { product_price, ...rest } = prevFilters || {};
                    return rest;
                  }
                  return {
                    ...prevFilters,
                    product_price: value,
                  };
                });
              }}
            />
          </AccordionDetails>
        </Accordion>
      </Box>
      <Box sx={{ px: 2, my: 1 }}>
        <Accordion sx={{ border: `1px solid ${theme.palette.grey[300]}` }}>
          <AccordionSummary
            expandIcon={
              <Iconify icon={"mdi:arrow-down-drop"} width={25} height={25} />
            }
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Typography sx={{ fontFamily: theme.typography.fontFamily }}>Product Ranking</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ProductRankingSelector
              value={localFilters?.product_rank}
              onSelect={(value) => {
                setLocalFilters((prevFilters: any) => ({
                  ...prevFilters,
                  product_rank: value,
                }));
              }}
            />
          </AccordionDetails>
        </Accordion>
      </Box>
      <DialogActions sx={{ mb: 10 }}>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => handleSaveClick(localFilters)}
          disabled={
            !localFilters || Object.keys(localFilters).length === 0
              ? true
              : false
          }
        >
          Apply
        </Button>
      </DialogActions>
    </Box>
  );
}
